import { createEffect, createMemo, For, Show, type Accessor, type JSX } from "solid-js"
import {
  DragDropProvider,
  DragDropSensors,
  DragOverlay,
  SortableProvider,
  closestCenter,
  type DragEvent,
} from "@thisbeyond/solid-dnd"
import { ConstrainDragXAxis } from "@/utils/solid-dnd"
import { IconButton } from "@openanalyst-ai/ui/icon-button"
import { type IconProps } from "@openanalyst-ai/ui/icon"
import { Tooltip, TooltipKeybind } from "@openanalyst-ai/ui/tooltip"
import { type LocalProject } from "@/context/layout"

export const SidebarContent = (props: {
  mobile?: boolean
  opened: Accessor<boolean>
  aimMove: (event: MouseEvent) => void
  projects: Accessor<LocalProject[]>
  renderProject: (project: LocalProject) => JSX.Element
  renderProjectApps?: (project: LocalProject) => JSX.Element
  renderProjectDropdown?: () => JSX.Element
  railActionIcon?: Accessor<IconProps["name"]>
  railActionLabel?: Accessor<string>
  railActionKeybind?: Accessor<string | undefined>
  railActionActive?: Accessor<boolean>
  railActionDisabled?: Accessor<boolean>
  onRailAction?: () => void
  handleDragStart: (event: unknown) => void
  handleDragEnd: () => void
  handleDragOver: (event: DragEvent) => void
  openProjectLabel: JSX.Element
  openProjectKeybind: Accessor<string | undefined>
  onOpenProject: () => void
  renderProjectOverlay: () => JSX.Element
  settingsLabel: Accessor<string>
  settingsKeybind: Accessor<string | undefined>
  onOpenSettings: () => void
  smartworkLabel?: Accessor<string>
  onOpenSmartwork?: () => void
  // Dev-only: opens the in-development plugin-app (the iframe-mounted dev app).
  // Reusable — points at whichever plugin-app is currently being built.
  renderPanel: () => JSX.Element
}): JSX.Element => {
  const expanded = createMemo(() => !!props.mobile || props.opened())
  const placement = () => (props.mobile ? "bottom" : "right")
  let panel: HTMLDivElement | undefined

  createEffect(() => {
    const el = panel
    if (!el) return
    if (expanded()) {
      el.removeAttribute("inert")
      return
    }
    el.setAttribute("inert", "")
  })

  return (
    <div class="flex h-full w-full min-w-0 overflow-hidden">
      <div
        data-component="sidebar-rail"
        class="w-16 shrink-0 bg-background-base flex flex-col items-center overflow-hidden"
        onMouseMove={props.aimMove}
      >
        <div class="flex-1 min-h-0 w-full">
          {props.renderProjectDropdown ? (
            <div
              data-sidebar-project-list=""
              data-sidebar-scrollbar=""
              class="h-full w-full flex flex-col items-center gap-3 px-3 py-3 overflow-y-auto"
            >
              {props.renderProjectDropdown()}
              <Show when={props.onRailAction}>
                {(onRailAction) => (
                  <TooltipKeybind
                    placement={placement()}
                    title={props.railActionLabel?.() ?? ""}
                    keybind={props.railActionKeybind?.() ?? ""}
                  >
                    <IconButton
                      icon={props.railActionIcon?.() ?? "folder-add-left"}
                      data-sidebar-rail-action="project-open"
                      variant="ghost"
                      size="large"
                      disabled={props.railActionDisabled?.()}
                      onClick={onRailAction()}
                      aria-label={props.railActionLabel?.()}
                      aria-current={props.railActionActive?.() ? "page" : undefined}
                    />
                  </TooltipKeybind>
                )}
              </Show>
            </div>
          ) : (
            <DragDropProvider
              onDragStart={props.handleDragStart}
              onDragEnd={props.handleDragEnd}
              onDragOver={props.handleDragOver}
              collisionDetector={closestCenter}
            >
              <DragDropSensors />
              <ConstrainDragXAxis />
              <div
                data-sidebar-project-list=""
                data-sidebar-scrollbar=""
                class="h-full w-full flex flex-col items-center gap-3 px-3 py-3 overflow-y-auto"
              >
                <SortableProvider ids={props.projects().map((p) => p.worktree)}>
                  <For each={props.projects()}>
                    {(project) => (
                      <>
                        {props.renderProject(project)}
                        {props.renderProjectApps?.(project)}
                      </>
                    )}
                  </For>
                </SortableProvider>
                <Tooltip
                  placement={placement()}
                  value={
                    <div class="flex items-center gap-2">
                      <span>{props.openProjectLabel}</span>
                      <Show when={!props.mobile && !!props.openProjectKeybind()}>
                        <span class="text-icon-base text-12-medium">{props.openProjectKeybind()}</span>
                      </Show>
                    </div>
                  }
                >
                  <IconButton
                    icon="folder-add-left"
                    data-sidebar-rail-action="project-open"
                    variant="secondary"
                    size="large"
                    onClick={props.onOpenProject}
                    aria-label={typeof props.openProjectLabel === "string" ? props.openProjectLabel : undefined}
                  />
                </Tooltip>
              </div>
              <DragOverlay>{props.renderProjectOverlay()}</DragOverlay>
            </DragDropProvider>
          )}
        </div>
        <div data-sidebar-rail-bottom="" class="shrink-0 w-full pt-3 pb-6 flex flex-col items-center gap-2">
          <Show when={props.onOpenSmartwork}>
            <Tooltip placement={placement()} value={props.smartworkLabel?.() ?? "Smartwork"}>
              <IconButton
                icon="models"
                data-sidebar-rail-action="smartwork"
                variant="ghost"
                size="large"
                onClick={props.onOpenSmartwork!}
                aria-label={props.smartworkLabel?.() ?? "Smartwork"}
              />
            </Tooltip>
          </Show>
          <TooltipKeybind placement={placement()} title={props.settingsLabel()} keybind={props.settingsKeybind() ?? ""}>
            <IconButton
              icon="settings-gear"
              data-sidebar-rail-action="settings"
              variant="ghost"
              size="large"
              onClick={props.onOpenSettings}
              aria-label={props.settingsLabel()}
            />
          </TooltipKeybind>
        </div>
      </div>

      <div
        ref={(el) => {
          panel = el
        }}
        classList={{ "flex-1 flex h-full min-h-0 min-w-0 overflow-hidden": true, "pointer-events-none": !expanded() }}
        aria-hidden={!expanded()}
      >
        {props.renderPanel()}
      </div>
    </div>
  )
}
